import React, { useState } from "react";
import styled from "styled-components";
import { ICON_TYPE } from "../../../configs/constants/IconsTypes";
import Flex from "../../../components/Layout/Flex";
import AppText from "../../../components/Core/AppText/AppText";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "../../../components/Shared/LoadingIndicator/LoadingIndicator";
import { useMediaStore } from "../../../stores/media.store";

interface ProfilePictureProps {
	name?: string;
	placeholder?: string;
	onUploadProfilePicture: Function;
}
interface PictureWrapperProps {
	src?: string;
}

interface PictureProps {
	hover: boolean;
	hasImage?: boolean;
}

const ProfilePicture: React.FC<ProfilePictureProps> = ({
	name,
	placeholder,
	onUploadProfilePicture,
}) => {
	const mediaStore = useMediaStore();

	const { profilePicture, loading } = mediaStore;

	const { t } = useTranslation();

	const [isOnHover, setHover] = useState<boolean>(false);

	const placeHolderImageSrc = placeholder || ICON_TYPE.IMAGE;

	const uploadImage = () => {
		if (inputRef) {
			inputRef.click();
		}
	};

	const removeImage = () => {
		if (profilePicture) {
			mediaStore.removeImage();
			setHover(false);
			onUploadProfilePicture();
		}
	};

	const viewImage = () => {
		if (profilePicture) window.open(profilePicture, "_blank");
	};

	const settings = [
		{ icon: ICON_TYPE.IMAGE_ICON, text: "VIEW", action: () => viewImage() },
		{
			icon: ICON_TYPE.UPLOAD_ICON,
			text: "UPLOAD",
			action: () => uploadImage(),
		},
		{
			icon: ICON_TYPE.REMOVE_ICON,
			text: "REMOVE",
			action: () => removeImage(),
		},
	];

	let inputRef: HTMLInputElement | null;

	const handleFileSelection = (files: any, e: any) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}

		const file = files[0];

		const reader = new FileReader();

		reader.onload = async (e: any) => {
			let result = await mediaStore.uploadProfilePicture(files.item(0));
			if (!!result) onUploadProfilePicture(result);
		};

		reader.readAsDataURL(file);
	};

	return (
		<PictureWrapper>
			{loading ? (
				<LoadingIndicator showText={false} />
			) : (
				<ProfilePictureWrapper
					onMouseEnter={() => setHover(true)}
					onMouseLeave={() => setHover(false)}
				>
					<input
						type="file"
						id="file"
						accept="image/*"
						ref={(ref) => (inputRef = ref)}
						style={{ display: "none" }}
						onChange={(e) => handleFileSelection(e.target.files, e)}
					/>

					<ProfilePictureContainer hover={isOnHover}>
						<Flex direction="column" justifyContent="space-around" height="80%" margin="1rem 0 0 0">
							{settings.map((s) => {
								return (
									<PictureEditWrapper key={s.text} onClick={s.action}>
										<img alt={name} src={s.icon} />
										<AppText size="s" color="white" weight="medium" margin="0 0 0 0.5rem">
											{t(s.text as any)}
										</AppText>
									</PictureEditWrapper>
								);
							})}
						</Flex>
					</ProfilePictureContainer>

					<PlaceholderImage
						alt="image"
						hasImage={!!profilePicture}
						src={profilePicture ? profilePicture + "?height=350" : placeHolderImageSrc}
						hover={isOnHover}
					/>
					{!profilePicture && !isOnHover && <PlaceholderText>{name}</PlaceholderText>}
				</ProfilePictureWrapper>
			)}
		</PictureWrapper>
	);
};
export default ProfilePicture;

const PictureWrapper = styled.div`
	height: 10.625rem;
	width: 7.5rem;
	border-radius: 0.1875rem;
	border: 0.1875rem solid white;
	display: flex;
	justify-content: center;
	align-items: center;
`;

const ProfilePictureWrapper = styled.div<PictureWrapperProps>`
	display: flex;
	background: ${(props) =>
		props.src ? props.theme.colors.COLOR_WHITE : props.theme.colors.COLOR_PLACEHOLDER_GRAY};
	box-sizing: border-box;
	box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.5);
	cursor: pointer;
	height: 10.25rem;
	width: 7.05rem;
	justify-content: center;
	position: relative;
`;

const PlaceholderImage = styled.img<PictureProps>`
	padding: ${(props) => (props.hasImage ? "" : "1.5rem")};
	height: ${(props) => (props.hasImage ? "inherit" : "7.5625rem")};
	display: ${(props) => (props.hover ? "none" : "block")};
	width: inherit;
	object-fit: cover;
`;

const ProfilePictureContainer = styled.div<PictureProps>`
	display: ${(props) => (props.hover ? "block" : "none")};
	background: black;
	width: 100%;
	opacity: 0.5;
`;

const PictureEditWrapper = styled.div`
	display: flex;
	margin: 0 0 0 1rem;
`;

const PlaceholderText = styled.span`
	position: absolute;
	top: 7.25rem;
	font-size: 1.5rem;
	color: ${(props) => props.theme.colors.COLOR_GRAY_TEXT};
	font-weight: ${(props) => props.theme.fontWeight.FONT_WEIGHT_MEDIUM};
`;
